/**
 * AuthLayout — centered card on a warm-tinted background, used for the
 * unauthenticated flow (login / invite-accept / password-reset).
 *
 * No nav, no topbar, no sidebar — single column, focused on the form. The
 * "АРИС" brand-mark renders as an inline SVG (Q54 — Fraunces is allowed
 * only as a hand-authored asset; webfont has no Cyrillic coverage).
 *
 * Outlet is included so this layout can be used as a route element. For
 * direct embedding (e.g. tests) callers can pass `children` instead.
 */
import * as React from 'react';
import { Outlet } from 'react-router-dom';

import { BrandMark } from '@/components/brand/brand-mark';
import { cn } from '@/lib/utils';

export interface AuthLayoutProps {
  /** When provided, rendered in place of <Outlet />. Useful for tests. */
  children?: React.ReactNode;
  className?: string;
}

export function AuthLayout({ children, className }: AuthLayoutProps): React.ReactElement {
  return (
    <div
      data-testid="auth-layout"
      className={cn(
        'min-h-screen w-full flex flex-col items-center justify-center bg-surface px-4 py-10',
        className,
      )}
    >
      <header className="mb-8 select-none">
        <BrandMark size="lg" withText subText="Платформа управления проектами" />
      </header>
      <main className="w-full max-w-md rounded-md border border-line bg-surface-2 p-6 shadow-1">
        {children ?? <Outlet />}
      </main>
      <footer className="mt-6 text-xs text-muted">Система управления загрузкой</footer>
    </div>
  );
}
